import { Map, MapMobile } from '../Map'
import Table from '../Table'
import type { Definition, Organization } from '../../types'
import { group, max, scaleSqrt } from 'd3'
import { useLocation } from 'react-router-dom'
import { MOBILE_WIDTH, RoutePath } from '../../constants'
import { useState } from 'react'
import { DetailsModal, DetailsModalMobile } from './components'
import { useWindowSize } from 'react-use'

interface MapTableContainerProps {
  organizations: Organization[]
  initiatives: Organization[]
  definitions: Definition[]
  explainerStep: number
  hasSeenInitialMapExplainer: boolean
  hideInitialMapExplainer: () => void
  onExplainerStepChange: (step: number) => void
}

const getOptionIds = (
  options: string[],
  definitions: Definition[],
  label: string
) => {
  return options.map((d) => {
    return definitions
      .filter((f) => f.id.startsWith(label))
      .find((def) => def.display_label === d)
      ?.id.replace(label, '')
  })
}

function MapTableContainer({
  organizations,
  initiatives,
  definitions,
  explainerStep,
  onExplainerStepChange,
  hasSeenInitialMapExplainer,
  hideInitialMapExplainer,
}: MapTableContainerProps) {
  const { pathname: viewType } = useLocation()
  const { width } = useWindowSize()

  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null)
  const [cityOrganizations, setCityOrganizations] = useState<Organization[]>([])
  const handleOrganizationClick = (
    organization: Organization | null,
    extraOrganizations: Organization[]
  ) => {
    setSelectedOrganization(organization)
    setCityOrganizations(extraOrganizations)
  }
  const handleCloseModal = () => {
    setSelectedOrganization(null)
    setCityOrganizations([])
  }

  const [isInitCommTask, setIsInitCommTask] = useState<boolean>(false)
  const handleIsInitCommTaskChange = (checked: boolean) =>
    setIsInitCommTask(checked)

  //organizations
  const [selectedGeoScopeOptions, setSelectedGeoScopeOptions] = useState<
    string[]
  >([])
  const handleSelectedGeoScopeOptionsChange = (options: string[]) =>
    setSelectedGeoScopeOptions(options)
  const [selectedFocusOptions, setSelectedFocusOptions] = useState<string[]>([])
  const handleSelectedFocusOptionsChange = (options: string[]) =>
    setSelectedFocusOptions(options)
  const [selectedStrategyOptions, setSelectedStrategyOptions] = useState<
    string[]
  >([])
  const handleSelectedStrategyOptionsChange = (options: string[]) =>
    setSelectedStrategyOptions(options)
  const [liableInstitution, setLiableInstitution] = useState<string>('all')
  const handleLiableInstitutionChange = (option: string) =>
    setLiableInstitution(option)

  const focusOptionsForFilter = getOptionIds(
    selectedFocusOptions,
    definitions,
    'focus:'
  )

  const geoScopeOptionsForFilter = getOptionIds(
    selectedGeoScopeOptions,
    definitions,
    'geo_scope:'
  )

  const strategyOptionsForFilter = getOptionIds(
    selectedStrategyOptions,
    definitions,
    'strategy:'
  )

  const filteredOrganizations = organizations.filter((organization) => {
    return (
      (geoScopeOptionsForFilter.length
        ? (organization.geo_scope || []).some((d) =>
            geoScopeOptionsForFilter.includes(d)
          )
        : true) &&
      (focusOptionsForFilter.length
        ? (organization.focus || []).some((d) =>
            focusOptionsForFilter.includes(d)
          )
        : true) &&
      (strategyOptionsForFilter.length
        ? (organization.strategy || []).some((d) =>
            strategyOptionsForFilter.includes(d)
          )
        : true) &&
      (liableInstitution !== 'all'
        ? (organization.target || []).some((d) => {
            return d.includes(`${liableInstitution}:`.toLowerCase())
          })
        : true) &&
      (isInitCommTask ? organization.initCommTask === isInitCommTask : true)
    )
  })

  //iniitiatives
  const [selectedStageOptions, setSelectedStageOptions] = useState<string[]>([])
  const handleSelectedStageOptionsChange = (options: string[]) =>
    setSelectedStageOptions(options)

  const [selectedRepairOptions, setSelectedRepairOptions] = useState<string[]>(
    []
  )
  const handleSelectedRepairOptionsChange = (options: string[]) =>
    setSelectedRepairOptions(options)

  const [
    selectedInitiativeTypeCheckboxes,
    setSelectedInitiativeTypeCheckboxes,
  ] = useState<{ [key: string]: boolean }>({
    government: true,
    academy: true,
    faith_based: true,
    descendant: true,
  })

  const [selectedInitiativeTypeOptions, setSelectedInitiativeTypeOptions] =
    useState<string[]>([])
  const handleSelectedInitiativeTypeOptionsChange = (options: string[]) => {
    setSelectedInitiativeTypeOptions(options)
  }

  const stageOptionsForFilter = getOptionIds(
    selectedStageOptions,
    definitions,
    'stage:'
  )

  const repairOptionsForFilter = getOptionIds(
    selectedRepairOptions,
    definitions,
    'repair:'
  )

  // const initiativeTypeOptionsForFilter = getOptionIds(
  //   selectedInitiativeTypeOptions,
  //   definitions,
  //   'initiative_type:'
  // )
  const filteredInitiatives = initiatives.filter((initiative) => {
    return (
      (stageOptionsForFilter.length
        ? (initiative.stage || []).some((d) =>
            stageOptionsForFilter.includes(d)
          )
        : true) &&
      (repairOptionsForFilter.length
        ? (initiative.repair || []).some((d) =>
            repairOptionsForFilter.includes(d)
          )
        : true) &&
      //this one is handled differently because of checkboxes --> it gets put in the correct form in FilterSection
      (selectedInitiativeTypeOptions?.length
        ? (initiative.initiative_type || []).some((d) =>
            selectedInitiativeTypeOptions.includes(d)
          )
        : true)
    )
  })

  const [display, setDisplay] = useState<string>('all')
  const showBoth = display === 'all'
  const showOrgs = display === 'orgs'
  //const showInits = display === 'inits'
  const handleDisplay = (value: string) => {
    setDisplay(value)
    setSelectedGeoScopeOptions([])
    setSelectedFocusOptions([])
    setSelectedStrategyOptions([])
    setLiableInstitution('all')
    setSelectedStageOptions([])
    setSelectedRepairOptions([])
    setSelectedInitiativeTypeCheckboxes({
      government: true,
      academy: true,
      faith_based: true,
      descendant: true,
    })
    setSelectedInitiativeTypeOptions([
      'government',
      'academy',
      'faith_based',
      'descendant',
    ])
  }
  const reparationsGroup = showBoth
    ? organizations.concat(initiatives)
    : showOrgs
    ? organizations
    : initiatives
  const filteredReparationsGroup = showBoth
    ? filteredOrganizations.concat(filteredInitiatives)
    : showOrgs
    ? filteredOrganizations
    : filteredInitiatives

  const groupedByAllCities = group(reparationsGroup, (d) => d['city state'])
  const groupedByCity = group(filteredReparationsGroup, (d) => d['city state'])

  const allCities = Array.from(groupedByAllCities)
    .filter(([key]) => Boolean(key))
    .map(([key, values]) => {
      return {
        name: key,
        coordinates: [values[0].long, values[0].lat],
        length: values.length,
      }
    })

  const rScale = scaleSqrt()
    .domain([0, max(allCities, (d) => d.length) as number])
    .range([0.5, 20])
  return (
    <section id="map_table_container">
      {viewType === RoutePath.Map ? (
        <>
          <div className="hidden sm:block">
            <Map
              display={display}
              setDisplay={setDisplay}
              handleDisplay={handleDisplay}
              data={groupedByCity}
              rScale={rScale}
              definitions={definitions}
              globalGeoScopeOptions={selectedGeoScopeOptions}
              onSelectedGeoScopeOptionsChange={setSelectedGeoScopeOptions}
              globalFocusOptions={selectedFocusOptions}
              onSelectedFocusOptionsChange={setSelectedFocusOptions}
              globalStrategyOptions={selectedStrategyOptions}
              onSelectedStrategyOptionsChange={setSelectedStrategyOptions}
              globalLiableInstitution={liableInstitution}
              onLiableInstitutionChange={setLiableInstitution}
              globalInitCommTask={isInitCommTask}
              onInitCommTaskChange={handleIsInitCommTaskChange}
              globalStageOptions={selectedStageOptions}
              onSelectedStageOptionsChange={setSelectedStageOptions}
              globalRepairOptions={selectedRepairOptions}
              onSelectedRepairOptionsChange={setSelectedRepairOptions}
              globalInitiativeTypeCheckboxes={selectedInitiativeTypeCheckboxes}
              onSelectedInitiativeTypeCheckboxesChange={
                setSelectedInitiativeTypeCheckboxes
              }
              globalInitiativeTypeOptions={selectedInitiativeTypeOptions}
              onSelectedInitiativeTypeOptionsChange={
                setSelectedInitiativeTypeOptions
              }
              onOrganizationClick={handleOrganizationClick}
              explainerStep={explainerStep}
              onExplainerStepChange={onExplainerStepChange}
              hasSeenInitialMapExplainer={hasSeenInitialMapExplainer}
              hideInitialMapExplainer={hideInitialMapExplainer}
            />
          </div>
          <div className="block sm:hidden">
            <MapMobile
              display={display}
              setDisplay={setDisplay}
              handleDisplay={handleDisplay}
              data={groupedByCity}
              rScale={rScale}
              definitions={definitions}
              globalGeoScopeOptions={selectedGeoScopeOptions}
              onSelectedGeoScopeOptionsChange={
                handleSelectedGeoScopeOptionsChange
              }
              globalFocusOptions={selectedFocusOptions}
              onSelectedFocusOptionsChange={handleSelectedFocusOptionsChange}
              globalStrategyOptions={selectedStrategyOptions}
              onSelectedStrategyOptionsChange={
                handleSelectedStrategyOptionsChange
              }
              globalLiableInstitution={liableInstitution}
              onLiableInstitutionChange={handleLiableInstitutionChange}
              globalInitCommTask={isInitCommTask}
              onInitCommTaskChange={handleIsInitCommTaskChange}
              globalStageOptions={selectedStageOptions}
              onSelectedStageOptionsChange={handleSelectedStageOptionsChange}
              globalRepairOptions={selectedRepairOptions}
              onSelectedRepairOptionsChange={handleSelectedRepairOptionsChange}
              globalInitiativeTypeCheckboxes={selectedInitiativeTypeCheckboxes}
              onSelectedInitiativeTypeCheckboxesChange={
                setSelectedInitiativeTypeCheckboxes
              }
              globalInitiativeTypeOptions={selectedInitiativeTypeOptions}
              onSelectedInitiativeTypeOptionsChange={
                handleSelectedInitiativeTypeOptionsChange
              }
              onOrganizationClick={handleOrganizationClick}
              explainerStep={explainerStep}
              onExplainerStepChange={onExplainerStepChange}
              hasSeenInitialMapExplainer={hasSeenInitialMapExplainer}
              hideInitialMapExplainer={hideInitialMapExplainer}
            />
          </div>
        </>
      ) : (
        <Table
          data={organizations.concat(initiatives)}
          definitions={definitions}
          onOrganizationClick={handleOrganizationClick}
          explainerStep={explainerStep}
          onExplainerStepChange={onExplainerStepChange}
        />
      )}
      {selectedOrganization ? (
        width < MOBILE_WIDTH ? (
          <DetailsModalMobile
            organization={selectedOrganization}
            definitions={definitions}
            onCloseModal={handleCloseModal}
          />
        ) : (
          <DetailsModal
            organization={selectedOrganization}
            definitions={definitions}
            cityOrganizations={cityOrganizations}
            onCloseModal={handleCloseModal}
            onOrganizationClick={handleOrganizationClick}
          />
        )
      ) : null}
    </section>
  )
}

export default MapTableContainer
